/// <reference lib="dom" />
/**
 * Canvas2D draw helpers for solid-earth ground fill and pit abyss gradients.
 * Extracted from webTileDraw.web.ts to keep files under the 300-line limit.
 *
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 *
 * @module webGroundDraw
 */

import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
  TILE_SIZE,
} from '../../constants/gameConstants';
import type { TiledMap, TiledTileLayer } from './TileMapTypes';

// DECISION: Cache the ground-surface row and gap columns to avoid re-scanning every frame.
let _cachedGroundTopY: number | null = null;
// DECISION: Cache key is the TiledMap object reference. This correctly
// invalidates when a different level of the same dimensions is loaded,
// unlike a width×height string key.
let _cachedGroundMapRef: TiledMap | null = null;
/** Columns that are pits (no terrain in the ground rows). */
let _cachedGapCols: boolean[] = [];

/**
 * Draw a solid earth fill behind the tile terrain area, skipping pit gaps.
 *
 * Hides parallax backgrounds behind tiles so grass/terrain sits on solid
 * ground instead of floating over parallax hills. The fill starts at the
 * ground surface row and extends to the viewport bottom.
 *
 * Pit gaps (columns with no terrain in ground rows) are left unfilled so the
 * parallax background shows through, with a fade-to-black gradient at the
 * bottom to create a cliff/abyss effect.
 *
 * The ground surface is detected as the topmost row from the bottom with
 * ≥50% tile coverage — this distinguishes the main ground from sparse
 * floating platforms higher up.
 */
export function drawGroundFill(
  ctx: CanvasRenderingContext2D,
  tiledMap: TiledMap,
  _camX: number,
  _camY: number,
): void {
  if (_cachedGroundMapRef !== tiledMap) {
    _cachedGroundTopY = null;
    _cachedGroundMapRef = tiledMap;
    _cachedGapCols = new Array(tiledMap.width).fill(false) as boolean[];
    const terrain = tiledMap.layers.find(
      (l): l is TiledTileLayer => l.type === 'tilelayer' && l.name === 'Terrain',
    );
    if (terrain) {
      // DECISION: Scan from bottom upward. The first row with ≥50% coverage is
      // ground surface; rows above it with <50% are floating platforms.
      const threshold = tiledMap.width * 0.5;
      for (let row = tiledMap.height - 1; row >= 0; row--) {
        let count = 0;
        for (let col = 0; col < tiledMap.width; col++) {
          if (terrain.data[row * terrain.width + col]) count++;
        }
        if (count >= threshold) {
          _cachedGroundTopY = row;
        } else if (_cachedGroundTopY !== null) {
          break; // Found a sparse row above the ground block — stop.
        }
      }

      // DECISION: Detect pit columns — columns where ALL ground rows lack terrain.
      // These gaps let parallax show through with a cliff-edge effect.
      if (_cachedGroundTopY !== null) {
        for (let col = 0; col < tiledMap.width; col++) {
          let hasGround = false;
          for (let row = _cachedGroundTopY; row < tiledMap.height; row++) {
            if (terrain.data[row * terrain.width + col]) {
              hasGround = true;
              break;
            }
          }
          _cachedGapCols[col] = !hasGround;
        }
      }
    }
  }

  if (_cachedGroundTopY === null) return;

  // DECISION: Ground fill removed — tiles themselves provide the terrain
  // visuals; no solid colour rectangle behind them. Parallax/sky shows
  // through any transparent tile gaps, which is the intended look.
}

/**
 * Draw a fade-to-black gradient inside pit gaps, creating a cliff/abyss look.
 *
 * Called after tile layers so the gradient overlays the parallax background
 * visible through the gaps. The gradient goes from transparent at the ground
 * surface to opaque black at the viewport bottom.
 */
export function drawPitGradients(
  ctx: CanvasRenderingContext2D,
  tiledMap: TiledMap,
  camX: number,
  camY: number,
): void {
  if (_cachedGroundTopY === null) return;

  const fillStartY = Math.round(_cachedGroundTopY * TILE_SIZE - camY);
  if (fillStartY >= VIEWPORT_HEIGHT) return;

  const colStart = Math.max(0, Math.floor(camX / TILE_SIZE));
  const colEnd = Math.min(tiledMap.width - 1, Math.floor((camX + VIEWPORT_WIDTH) / TILE_SIZE));

  // Find contiguous gap spans and draw a gradient over each
  let gapStart = -1;
  for (let col = colStart; col <= colEnd + 1; col++) {
    const isGap = col <= colEnd && _cachedGapCols[col];
    if (isGap && gapStart < 0) {
      gapStart = col;
    } else if (!isGap && gapStart >= 0) {
      const sx = Math.round(gapStart * TILE_SIZE - camX);
      const ex = Math.round(col * TILE_SIZE - camX);
      const gradTop = fillStartY;
      const gradH = VIEWPORT_HEIGHT - gradTop;

      const grad = ctx.createLinearGradient(0, gradTop, 0, gradTop + gradH);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(0.6, 'rgba(0,0,0,0.5)');
      grad.addColorStop(1, 'rgba(0,0,0,0.95)');
      ctx.fillStyle = grad;
      ctx.fillRect(sx, gradTop, ex - sx, gradH);
      gapStart = -1;
    }
  }
}
